import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import SafeIcon from '../common/SafeIcon';
import AddressInput from '../components/representatives/AddressInput';
import RepresentativeCard from '../components/representatives/RepresentativeCard';
import {
  normalizeAddressFromPlace,
  fetchCivicData,
  joinOfficesAndOfficials,
  categorizeRepresentatives,
  cacheRepresentatives,
  getCachedRepresentatives,
  clearCachedRepresentatives,
  formatDate,
  fetchRepresentativesWithFallback
} from '../utils/representativeUtils';
import * as FiIcons from 'react-icons/fi';

const { 
  FiMapPin, FiSearch, FiLoader, FiInfo, FiAlertCircle, FiCheck, FiShare2,
  FiTarget, FiHelpCircle, FiSave, FiRefreshCw, FiAlertTriangle, FiFlag 
} = FiIcons;

const RepresentativesLookup = () => {
  const { user } = useAuth();

  // State for the selected place
  const [selectedPlace, setSelectedPlace] = useState(null);
  // State for the normalized address
  const [normalizedAddress, setNormalizedAddress] = useState('');
  // State for loading and error states
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // State for representatives data
  const [representatives, setRepresentatives] = useState(null);
  const [districts, setDistricts] = useState(null);
  // State for metadata
  const [lastUpdated, setLastUpdated] = useState(null);
  // State for saved addresses
  const [savedAddresses, setSavedAddresses] = useState([]);
  const [showSavedAddresses, setShowSavedAddresses] = useState(false);
  // State to track if Google Maps API is loaded
  const [googleMapsLoaded, setGoogleMapsLoaded] = useState(false);
  // State to track if we're using mock data
  const [usingMockData, setUsingMockData] = useState(false);

  // Google Maps API key
  const API_KEY = import.meta.env.VITE_GOOGLE_MAPS_KEY || "AIzaSyC86MKVZQRT_PWcrEx3IJU418vo10I5iSs";

  // Load Google Maps API
  useEffect(() => {
    // Check if Google Maps API is already loaded
    if (window.google && window.google.maps && window.google.maps.places) {
      setGoogleMapsLoaded(true);
      return;
    }

    // Create script element for Google Maps API
    const googleMapScript = document.createElement('script');
    googleMapScript.src = `https://maps.googleapis.com/maps/api/js?key=${API_KEY}&libraries=places`;
    googleMapScript.async = true;
    googleMapScript.defer = true;

    // Set up callback for when the script loads
    googleMapScript.addEventListener('load', () => {
      console.log("Google Maps API loaded successfully");
      setGoogleMapsLoaded(true);
    });

    // Handle errors
    googleMapScript.addEventListener('error', (e) => {
      console.error('Failed to load Google Maps API:', e);
      setError('Failed to load Google Maps API. Please try again later.');
    });

    // Append the script to the document
    document.head.appendChild(googleMapScript);

    // Clean up
    return () => {
      // Remove script if component unmounts before script loads
      if (document.head.contains(googleMapScript)) {
        document.head.removeChild(googleMapScript);
      }
    };
  }, [API_KEY]);

  // Load saved addresses from localStorage on component mount
  useEffect(() => {
    const loadSavedAddresses = () => {
      try {
        const saved = localStorage.getItem('savedAddresses');
        if (saved) {
          const parsed = JSON.parse(saved);
          if (Array.isArray(parsed)) {
            setSavedAddresses(parsed);
          }
        }
      } catch (e) {
        console.error("Failed to load saved addresses", e);
      }
    };

    loadSavedAddresses();
  }, []);

  // Handle place selection from AddressInput
  const handlePlaceSelected = (place) => {
    console.log("Place selected:", place);
    setSelectedPlace(place);
    setNormalizedAddress(place.formatted_address || '');
    setError(null);
    setUsingMockData(false);

    // Check for cached data
    const cachedData = getCachedRepresentatives(place.place_id);
    if (cachedData) {
      console.log("Using cached data for place:", place.place_id);
      setRepresentatives(cachedData.representatives);
      setDistricts(cachedData.districts);
      setLastUpdated(cachedData.timestamp);
      setUsingMockData(cachedData.usingMockData || false);
      return;
    }
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    
    if (!selectedPlace) {
      setError("Please select a valid address from the suggestions");
      return;
    }

    await fetchRepresentatives();
  };

  // Fetch representatives data
  const fetchRepresentatives = async (refresh = false) => {
    if (!selectedPlace || !normalizedAddress) {
      setError("Please select a valid address");
      return;
    }

    setLoading(true);
    setError(null);
    setUsingMockData(false);

    try {
      // If refresh is true, clear cached data
      if (refresh) {
        clearCachedRepresentatives(selectedPlace.place_id);
      } else {
        // Check for cached data
        const cachedData = getCachedRepresentatives(selectedPlace.place_id);
        if (cachedData) {
          setRepresentatives(cachedData.representatives);
          setDistricts(cachedData.districts);
          setLastUpdated(cachedData.timestamp);
          setUsingMockData(cachedData.usingMockData || false);
          setLoading(false);
          return;
        }
      }

      // Get normalized address if not already set
      const address = normalizedAddress || await normalizeAddressFromPlace(selectedPlace);
      setNormalizedAddress(address);
      
      console.log("Fetching representatives for address:", address);
      
      // Try to fetch data from Civic API through our proxy, with fallback to mock data
      let civicData;
      let isUsingMockData = false;
      
      try {
        civicData = await fetchCivicData(address);
        console.log("Civic API response received:", civicData);
      } catch (apiError) {
        console.warn("Civic API unavailable, using mock data:", apiError.message);
        // Use fallback with mock data
        civicData = await fetchRepresentativesWithFallback(address);
        isUsingMockData = true;
        setUsingMockData(true);
        
        // Show specific error message
        setError(`API Error: ${apiError.message}. Showing sample data for demonstration.`);
      }
      
      // Process the response
      const joinedData = joinOfficesAndOfficials(civicData);
      const categorizedData = categorizeRepresentatives(joinedData);
      
      // Update state
      setRepresentatives(categorizedData);

      // Extract districts
      const districts = {
        congressional: null,
        stateSenate: null,
        stateHouse: null
      };

      // Extract congressional district from representatives
      if (categorizedData.federal.representatives.length > 0) {
        const rep = categorizedData.federal.representatives[0];
        if (rep.district) {
          districts.congressional = `${rep.district}`;
        }
      }

      // Extract state districts from state representatives
      categorizedData.state.statewide.forEach(rep => {
        if (rep.position === "State Senator" && rep.district) {
          districts.stateSenate = `State Senate District ${rep.district}`;
        } else if (rep.position === "State Representative" && rep.district) {
          districts.stateHouse = `State House District ${rep.district}`;
        }
      });

      setDistricts(districts);

      // Update last updated timestamp
      const now = Date.now();
      setLastUpdated(now);

      // Cache the data
      cacheRepresentatives(selectedPlace.place_id, {
        representatives: categorizedData,
        districts,
        timestamp: now,
        usingMockData: isUsingMockData
      });

    } catch (error) {
      console.error("Error fetching representatives:", error);
      setError(error.message || "Failed to fetch representatives. Please try a different address.");
    } finally {
      setLoading(false);
    }
  };

  // Save address to localStorage
  const saveAddress = () => {
    if (!selectedPlace || !normalizedAddress) return;

    try {
      const newAddress = {
        placeId: selectedPlace.place_id,
        address: normalizedAddress,
        timestamp: Date.now()
      };

      const newSavedAddresses = [...savedAddresses];

      // Don't add duplicates
      const existingIndex = newSavedAddresses.findIndex(addr => addr.placeId === newAddress.placeId);
      if (existingIndex >= 0) {
        newSavedAddresses[existingIndex] = newAddress;
      } else {
        newSavedAddresses.push(newAddress);
      }

      setSavedAddresses(newSavedAddresses);
      localStorage.setItem('savedAddresses', JSON.stringify(newSavedAddresses));
    } catch (e) {
      console.error("Failed to save address to localStorage", e);
    }
  };

  // Load a saved address
  const loadSavedAddress = (savedAddress) => {
    setSelectedPlace({ place_id: savedAddress.placeId });
    setNormalizedAddress(savedAddress.address);
    setShowSavedAddresses(false);

    // Check for cached data
    const cachedData = getCachedRepresentatives(savedAddress.placeId);
    if (cachedData) {
      setRepresentatives(cachedData.representatives);
      setDistricts(cachedData.districts);
      setLastUpdated(cachedData.timestamp);
      setUsingMockData(cachedData.usingMockData || false);
      return;
    }

    // If no cached data, fetch representatives
    fetchRepresentatives();
  };

  // Remove a saved address
  const removeSavedAddress = (addressToRemove) => {
    try {
      const newSavedAddresses = savedAddresses.filter(
        addr => addr.placeId !== addressToRemove.placeId
      );
      setSavedAddresses(newSavedAddresses);
      localStorage.setItem('savedAddresses', JSON.stringify(newSavedAddresses));
    } catch (e) {
      console.error("Failed to update saved addresses in localStorage", e);
    }
  };

  // Share representative info
  const shareRepresentative = (rep) => {
    if (navigator.share) {
      navigator.share({
        title: `${rep.name} - ${rep.position || rep.officeName}`,
        text: `Contact information for ${rep.name}, ${rep.position || rep.officeName}`,
        url: window.location.href,
      }).catch(err => {
        console.error("Error sharing:", err);
        copyToClipboard(rep);
      });
    } else {
      // Fallback for browsers that don't support Web Share API
      copyToClipboard(rep);
    }
  };

  // Helper function for clipboard copying
  const copyToClipboard = (rep) => {
    const shareText = `${rep.name} - ${rep.position || rep.officeName || rep.role || ''}\nContact: ${rep.phones?.[0] || ''}`;
    
    navigator.clipboard.writeText(shareText)
      .then(() => alert('Contact information copied to clipboard!'))
      .catch(err => {
        console.error("Clipboard write failed:", err);
        alert('Failed to copy contact information. Please try again.');
      });
  };

  // Report a data mismatch
  const reportMismatch = () => {
    if (!selectedPlace) return;
    
    const reportData = {
      placeId: selectedPlace.place_id,
      formattedAddress: normalizedAddress,
      timestamp: Date.now()
    };
    
    console.log("Reporting data mismatch:", reportData);
    alert("Thank you for reporting this issue. Our team will investigate the mismatch.");
    // In a real implementation, this would send the data to a server
  };

  // Refresh representatives data
  const refreshData = () => {
    if (!selectedPlace) return;
    fetchRepresentatives(true);
  };

  // Test API key with a sample address (1600 Pennsylvania Ave NW, Washington, DC)
  const testWithWhiteHouse = async () => {
    setLoading(true);
    setError(null);
    setUsingMockData(false);

    try {
      const testAddress = "1600 Pennsylvania Ave NW, Washington, DC 20500";
      setNormalizedAddress(testAddress);
      
      console.log("Testing Civic API with address:", testAddress);
      
      // Create a mock place object for the White House
      const mockPlace = {
        place_id: "ChIJ215e_Uq3t4kRtVqV-Wa0ndM", // White House place_id
        formatted_address: testAddress
      };
      
      setSelectedPlace(mockPlace);
      
      // Try to fetch data from Civic API through our proxy, with fallback
      let civicData;
      let isUsingMockData = false;
      
      try {
        civicData = await fetchCivicData(testAddress);
        console.log("Civic API test successful:", civicData);
        setError(null); // Clear any previous errors on success
      } catch (apiError) {
        console.warn("Civic API test failed, using mock data:", apiError.message);
        civicData = await fetchRepresentativesWithFallback(testAddress);
        isUsingMockData = true;
        setUsingMockData(true);
        setError(`API Test Result: ${apiError.message}. Showing sample data for the White House.`);
      }
      
      // Process the response
      const joinedData = joinOfficesAndOfficials(civicData);
      const categorizedData = categorizeRepresentatives(joinedData);
      
      // Update state
      setRepresentatives(categorizedData);

      // Extract districts (same as in fetchRepresentatives)
      const districts = {
        congressional: null,
        stateSenate: null,
        stateHouse: null
      };

      if (categorizedData.federal.representatives.length > 0) {
        const rep = categorizedData.federal.representatives[0];
        if (rep.district) {
          districts.congressional = `${rep.district}`;
        }
      }

      categorizedData.state.statewide.forEach(rep => {
        if (rep.position === "State Senator" && rep.district) {
          districts.stateSenate = `State Senate District ${rep.district}`;
        } else if (rep.position === "State Representative" && rep.district) {
          districts.stateHouse = `State House District ${rep.district}`;
        }
      });

      setDistricts(districts);

      // Update last updated timestamp
      const now = Date.now();
      setLastUpdated(now);

      // Cache the data
      cacheRepresentatives(mockPlace.place_id, {
        representatives: categorizedData,
        districts,
        timestamp: now,
        usingMockData: isUsingMockData
      });

    } catch (error) {
      console.error("Error testing Civic API:", error);
      setError(error.message || "Failed to test representatives lookup. Please check the configuration.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h1 className="text-4xl font-bold text-patriot-navy mb-4">Find Your Representatives</h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Discover who represents you at every level of government and connect directly with your elected officials.
          </p>
        </motion.div>

        {/* API Status Notice */}
        <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded-md mb-8">
          <div className="flex items-center">
            <SafeIcon icon={FiInfo} className="h-5 w-5 text-blue-500 mr-2" />
            <div>
              <p className="text-blue-700 font-medium">Live API Now Available!</p>
              <p className="text-blue-600 text-sm">
                The representatives lookup is now connected to the Google Civic Information API. 
                You can search for real representative data using any U.S. address.
              </p>
            </div>
          </div>
        </div>

        {/* Address Input Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="bg-white rounded-lg shadow-md p-6 mb-8"
        >
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-patriot-navy flex items-center">
              <SafeIcon icon={FiMapPin} className="h-5 w-5 mr-2" />
              Your Location
            </h2>
            <div className="flex space-x-2">
              {savedAddresses.length > 0 && (
                <div className="relative">
                  <button
                    onClick={() => setShowSavedAddresses(!showSavedAddresses)}
                    className="flex items-center text-sm text-patriot-red hover:text-patriot-red-dark"
                  >
                    <SafeIcon icon={FiTarget} className="h-4 w-4 mr-1" />
                    Saved Addresses
                  </button>
                  
                  {showSavedAddresses && (
                    <div className="absolute right-0 mt-2 w-64 bg-white rounded-md shadow-lg z-10 border border-gray-200">
                      <div className="py-1">
                        {savedAddresses.map((savedAddress, index) => (
                          <div key={index} className="flex items-center justify-between px-4 py-2 hover:bg-gray-100 cursor-pointer">
                            <button
                              onClick={() => loadSavedAddress(savedAddress)}
                              className="text-left text-sm text-gray-700 truncate flex-1"
                            >
                              {savedAddress.address}
                            </button>
                            <button
                              onClick={() => removeSavedAddress(savedAddress)}
                              className="text-red-500 hover:text-red-700 ml-2"
                            >
                              <SafeIcon icon={FiIcons.FiX} className="h-4 w-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          <form onSubmit={handleSubmit} className="relative mb-4">
            {googleMapsLoaded ? (
              <AddressInput
                onAddressSelected={handlePlaceSelected}
                disabled={loading}
                placeholder="Enter your full street address"
              />
            ) : (
              <div className="w-full relative">
                <SafeIcon
                  icon={FiMapPin}
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5"
                />
                <input
                  type="text"
                  disabled
                  placeholder="Loading Google Maps..."
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg bg-gray-50"
                />
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                  <SafeIcon icon={FiLoader} className="h-5 w-5 text-gray-400 animate-spin" />
                </div>
              </div>
            )}

            {normalizedAddress && (
              <div className="mt-2 flex items-center justify-between">
                <p className="text-xs text-gray-500 flex items-center">
                  <SafeIcon icon={FiCheck} className="h-3 w-3 mr-1 text-green-500" />
                  Address selected: {normalizedAddress}
                </p>
                <button
                  type="button"
                  onClick={saveAddress}
                  className="text-xs text-patriot-red hover:text-patriot-red-dark flex items-center"
                  title="Save this address"
                >
                  <SafeIcon icon={FiSave} className="h-3 w-3 mr-1" />
                  Save
                </button>
              </div>
            )}
          </form>

          <div className="flex justify-between items-center">
            <div className="text-sm text-gray-500 flex items-center">
              <SafeIcon icon={FiHelpCircle} className="h-4 w-4 mr-1" />
              Your address is only used to find your representatives
            </div>

            <div className="flex space-x-2">
              <button
                onClick={testWithWhiteHouse}
                className="flex items-center px-3 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors duration-200 text-sm"
              >
                <SafeIcon icon={FiInfo} className="h-4 w-4 mr-1" />
                Test with White House
              </button>

              <button
                onClick={handleSubmit}
                disabled={!selectedPlace || loading || !googleMapsLoaded}
                className="flex items-center px-4 py-2 bg-patriot-red text-white rounded-lg hover:bg-patriot-red-dark transition-colors duration-200 disabled:bg-gray-300"
              >
                {loading ? (
                  <>
                    <SafeIcon icon={FiLoader} className="h-4 w-4 mr-2 animate-spin" />
                    Finding Representatives...
                  </>
                ) : (
                  <>
                    <SafeIcon icon={FiSearch} className="h-4 w-4 mr-2" />
                    Find My Representatives
                  </>
                )}
              </button>
            </div>
          </div>
        </motion.div>

        {/* Loading State */}
        {loading && (
          <div className="flex flex-col items-center justify-center py-12">
            <SafeIcon icon={FiLoader} className="h-10 w-10 text-patriot-red animate-spin mb-4" />
            <p className="text-lg text-gray-600">Finding your representatives...</p>
          </div>
        )}

        {/* Error Message */}
        {error && !loading && (
          <div className={`border-l-4 p-4 rounded-md mb-8 ${
            usingMockData ? 'bg-yellow-50 border-yellow-500' : 'bg-red-50 border-red-500'
          }`}>
            <div className="flex items-center">
              <SafeIcon 
                icon={usingMockData ? FiInfo : FiAlertCircle} 
                className={`h-5 w-5 mr-2 ${usingMockData ? 'text-yellow-500' : 'text-red-500'}`} 
              />
              <div>
                <p className={`font-medium ${usingMockData ? 'text-yellow-700' : 'text-red-700'}`}>
                  {usingMockData ? 'Using Sample Data' : 'Error'}
                </p>
                <p className={usingMockData ? 'text-yellow-600' : 'text-red-600'}>{error}</p>
              </div>
            </div>
          </div>
        )}

        {/* Results Section */}
        {representatives && !loading && (
          <div className="mb-6">
            <div className="bg-white rounded-lg shadow-md p-4 mb-6">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between">
                <div className="mb-4 md:mb-0">
                  <h2 className="text-xl font-semibold text-patriot-navy">Your Representatives</h2>
                  <div className="flex items-center text-sm text-gray-500 space-x-2">
                    {lastUpdated && (
                      <span>Last updated: {formatDate(lastUpdated)}</span>
                    )}
                    <span>•</span>
                    <span>Source: {usingMockData ? 'Sample Data' : 'Google Civic Information API'}</span>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={refreshData}
                    className="flex items-center text-sm px-3 py-1 bg-blue-50 text-blue-600 rounded hover:bg-blue-100 transition-colors"
                  >
                    <SafeIcon icon={FiRefreshCw} className="h-4 w-4 mr-1" />
                    Refresh Data
                  </button>
                  <button
                    onClick={reportMismatch}
                    className="flex items-center text-sm px-3 py-1 bg-amber-50 text-amber-600 rounded hover:bg-amber-100 transition-colors"
                  >
                    <SafeIcon icon={FiFlag} className="h-4 w-4 mr-1" />
                    Report Mismatch
                  </button>
                </div>
              </div>
            </div>

            {/* Districts Info */}
            {districts && (
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="bg-white rounded-lg shadow-md p-6 mb-8"
              >
                <h2 className="text-xl font-semibold text-patriot-navy mb-4">Your Districts</h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {districts.congressional && (
                    <div className="p-3 bg-gray-50 rounded-lg">
                      <h3 className="font-medium text-gray-900">Congressional</h3>
                      <p className="text-gray-600 text-sm">{districts.congressional}</p>
                    </div>
                  )}
                  {districts.stateSenate && (
                    <div className="p-3 bg-gray-50 rounded-lg">
                      <h3 className="font-medium text-gray-900">State Senate</h3>
                      <p className="text-gray-600 text-sm">{districts.stateSenate}</p>
                    </div>
                  )}
                  {districts.stateHouse && (
                    <div className="p-3 bg-gray-50 rounded-lg">
                      <h3 className="font-medium text-gray-900">State House</h3>
                      <p className="text-gray-600 text-sm">{districts.stateHouse}</p>
                    </div>
                  )}
                </div>
              </motion.div>
            )}

            {/* Federal Representatives */}
            <div className="mb-12">
              <h2 className="text-2xl font-bold text-patriot-navy mb-6">Federal Representatives</h2>

              {/* U.S. Senators */}
              {representatives.federal.senators.length > 0 && (
                <div className="mb-8">
                  <h3 className="text-xl font-semibold text-patriot-navy mb-4">U.S. Senators</h3>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {representatives.federal.senators.map((senator, index) => (
                      <RepresentativeCard
                        key={`senator-${index}`}
                        rep={senator}
                        onShare={shareRepresentative}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* U.S. Representatives */}
              {representatives.federal.representatives.length > 0 && (
                <div className="mb-8">
                  <h3 className="text-xl font-semibold text-patriot-navy mb-4">U.S. House Representative</h3>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {representatives.federal.representatives.map((rep, index) => (
                      <RepresentativeCard
                        key={`rep-${index}`}
                        rep={rep}
                        onShare={shareRepresentative}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* State Representatives */}
            <div className="mb-12">
              <h2 className="text-2xl font-bold text-patriot-navy mb-6">State Representatives</h2>

              {/* Governor */}
              {representatives.state.governor && (
                <div className="mb-8">
                  <h3 className="text-xl font-semibold text-patriot-navy mb-4">Governor</h3>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <RepresentativeCard
                      rep={representatives.state.governor}
                      onShare={shareRepresentative}
                    />
                  </div>
                </div>
              )}

              {/* Other Statewide Officials */}
              {representatives.state.statewide.length > 0 && (
                <div className="mb-8">
                  <h3 className="text-xl font-semibold text-patriot-navy mb-4">State Officials</h3>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {representatives.state.statewide.map((official, index) => (
                      <RepresentativeCard
                        key={`state-${index}`}
                        rep={official}
                        onShare={shareRepresentative}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Local Representatives */}
            {representatives.local.length > 0 && (
              <div className="mb-12">
                <h2 className="text-2xl font-bold text-patriot-navy mb-6">Local Representatives</h2>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {representatives.local.map((official, index) => (
                    <RepresentativeCard
                      key={`local-${index}`}
                      rep={official}
                      onShare={shareRepresentative}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* No Results */}
        {!loading && !representatives && (
          <div className="text-center py-12">
            <SafeIcon icon={FiMapPin} className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Find Your Representatives</h3>
            <p className="text-gray-600 mb-4">
              Enter your complete address above to discover who represents you at every level of government.
            </p>
          </div>
        )}

        {/* FAQ Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="bg-white rounded-lg shadow-md p-6 mb-8"
        >
          <h2 className="text-xl font-semibold text-patriot-navy mb-6">Frequently Asked Questions</h2>
          <div className="space-y-4">
            <div className="border border-gray-200 rounded-lg p-4">
              <h3 className="font-medium text-patriot-navy mb-2">How accurate is this information?</h3>
              <p className="text-gray-600">
                This data comes directly from Google's Civic Information API, which is regularly updated.
                However, there may be occasional inaccuracies, especially after recent elections or redistricting.
                If you notice any discrepancies, please use the "Report Mismatch" button.
              </p>
            </div>
            <div className="border border-gray-200 rounded-lg p-4">
              <h3 className="font-medium text-patriot-navy mb-2">Is my address information stored?</h3>
              <p className="text-gray-600">
                Your address is only used to look up your representatives and is not permanently stored on our servers.
                If you choose to save an address, it's stored locally on your device for convenience.
              </p>
            </div>
            <div className="border border-gray-200 rounded-lg p-4">
              <h3 className="font-medium text-patriot-navy mb-2">Why are some representatives missing?</h3>
              <p className="text-gray-600">
                The API might not have complete information for all elected positions, particularly for local offices.
                For the most accurate information, visit your state or local government's official website.
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default RepresentativesLookup;